(() => {
  const log = () => {};
  const logError = () => {};
  let rootSyncScheduled = false;
  let rootSyncInProgress = false;
  const parentQuantitySnapshotByKey = new Map();

  const cartAddUrl =
    window.theme && window.theme.routes && window.theme.routes.cartAdd
      ? window.theme.routes.cartAdd
      : '/cart/add.js';
  const cartUpdateUrl =
    window.theme && window.theme.routes && window.theme.routes.cartUpdate
      ? window.theme.routes.cartUpdate
      : '/cart/update.js';

  const dispatchCartChange = () => {
    log('dispatchCartChange');
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  };

  const refreshCartDrawer = () => {
    log('refreshCartDrawer');
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true, cancelable: false }));
  };

  if (window.BWGiftWrapUpsellInit) {
    log('init skipped: already initialized');
    return;
  }
  window.BWGiftWrapUpsellInit = true;
  log('init done', { cartAddUrl, cartUpdateUrl });

  const openCartDrawer = (opener) => {
    log('openCartDrawer');
    document.dispatchEvent(
      new CustomEvent('dispatch:cart-drawer:open', {
        bubbles: true,
        cancelable: false,
        detail: { opener },
      })
    );
  };

  const setRootLoading = (root, isLoading) => {
    log('setRootLoading', {
      parentKey: root?.dataset?.parentKey,
      isLoading,
    });
    root.classList.toggle('is-loading', isLoading);
    root.querySelectorAll('button, input').forEach((el) => {
      el.disabled = isLoading;
    });
  };

  const addNestedWrap = async (variantId, parentKey, parentQuantity = 1) => {
    const normalizedParentQuantity = Math.max(1, Number(parentQuantity) || 1);

    log('addNestedWrap:start', {
      variantId,
      parentKey,
      parentQuantity: normalizedParentQuantity,
    });

    const linePayload = {
      id: Number(variantId),
      quantity: normalizedParentQuantity,
      parent_line_key: parentKey,
    };

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: [linePayload] }),
    });

    log('addNestedWrap:done', { ok: response.ok, status: response.status });
    return response.ok;
  };

  const fetchCart = async () => {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    }).then((res) => res.json());
  };

  const isChildOfParentKey = (cartItem, parentKey) => {
    const relation = cartItem && cartItem.parent_relationship ? cartItem.parent_relationship : null;
    if (!relation || !parentKey) return false;

    const candidates = [
      relation.parent_key,
      relation.parent_line_key,
      relation.cart_line_key,
      relation.parent_id,
      relation.parent && relation.parent.key,
      relation.parent && relation.parent.line_key,
      relation.parent && relation.parent.parent_key,
      relation.parent && relation.parent.id,
      relation.parent && relation.parent.line,
      relation.parent && relation.parent.cart_line_key,
    ]
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value));

    return candidates.includes(String(parentKey));
  };

  const getManagedVariantIds = (root) => {
    const variantIds = new Set();
    root.querySelectorAll('[data-gift-wrap-option]').forEach((option) => {
      const variantId = String(option.dataset.variantId || '').trim();
      if (variantId) {
        variantIds.add(variantId);
      }
    });
    return variantIds;
  };

  const getWrapChildrenForParent = (root, cartItems = []) => {
    const parentKey = root?.dataset?.parentKey || '';
    const managedVariantIds = getManagedVariantIds(root);
    if (!parentKey || !managedVariantIds.size) return [];

    return (cartItems || []).filter((cartItem) => {
      if (String(cartItem.key || '') === String(parentKey)) return false;
      if (!isChildOfParentKey(cartItem, parentKey)) return false;
      return managedVariantIds.has(String(cartItem.variant_id));
    });
  };

  const getTotalWrapQuantity = (wrapChildren = []) => {
    return (wrapChildren || []).reduce((sum, child) => {
      return sum + Math.max(0, Number(child.quantity) || 0);
    }, 0);
  };

  const getParentQuantity = (root) => {
    return Math.max(1, Number(root?.dataset?.parentQuantity || '1') || 1);
  };

  const didParentQuantityIncrease = (root) => {
    const parentKey = String(root?.dataset?.parentKey || '').trim();
    if (!parentKey) return false;

    const currentQuantity = getParentQuantity(root);
    const previousQuantity = parentQuantitySnapshotByKey.get(parentKey);
    parentQuantitySnapshotByKey.set(parentKey, currentQuantity);

    if (!Number.isFinite(previousQuantity)) return false;
    return currentQuantity > previousQuantity;
  };

  const syncGiftWrapRootSelection = (root, cartItems = [], shouldSyncQuantity = false) => {
    if (!root) return false;

    const wrapChildren = getWrapChildrenForParent(root, cartItems);
    const currentSelectedVariantId = String(root.dataset.selectedVariantId || '').trim();

    if (!wrapChildren.length) {
      root.dataset.selectedKeys = '';
      delete root.dataset.selectedKey;
      delete root.dataset.selectedVariantId;
      root.classList.remove('is-selected');

      const checkbox = root.querySelector('[data-gift-wrap-checkbox]');
      const selectedState = root.querySelector('[data-gift-wrap-selected]');
      if (checkbox) checkbox.checked = false;
      if (selectedState) selectedState.hidden = true;

      if (!currentSelectedVariantId) {
        setActiveOption(root, '');
      }
      return false;
    }

    const selectedMatch = currentSelectedVariantId
      ? wrapChildren.find((item) => String(item.variant_id) === currentSelectedVariantId)
      : null;
    const activeItem = selectedMatch || wrapChildren[0];
    const activeVariantId = String(activeItem.variant_id);
    const selectedKeysCsv = wrapChildren.map((item) => item.key).join(',');

    root.classList.add('is-selected');
    root.dataset.selectedKeys = selectedKeysCsv;
    root.dataset.selectedKey = activeItem.key;
    root.dataset.selectedVariantId = activeVariantId;

    const checkbox = root.querySelector('[data-gift-wrap-checkbox]');
    const panel = root.querySelector('[data-gift-wrap-panel]');
    const selectedState = root.querySelector('[data-gift-wrap-selected]');
    if (checkbox) checkbox.checked = true;
    if (panel) panel.hidden = true;
    if (selectedState) selectedState.hidden = false;

    setActiveOption(root, activeVariantId);

    return false;
  };

  const removeWrapsForParent = async (root, selectedKeys = [], keepVariantId = '') => {
    const parentKey = root.dataset.parentKey || '';
    const updates = {};
    const normalizedKeepVariantId = String(keepVariantId || '').trim();

    log('removeWrapsForParent:start', {
      parentKey,
      selectedKeys,
      keepVariantId: normalizedKeepVariantId,
    });

    (selectedKeys || []).forEach((key) => {
      if (key) {
        updates[key] = 0;
      }
    });

    const cart = await fetchCart();
    const wrapChildren = getWrapChildrenForParent(root, cart.items || []);
    wrapChildren.forEach((cartItem) => {
      if (normalizedKeepVariantId && String(cartItem.variant_id) === normalizedKeepVariantId) {
        return;
      }
      updates[cartItem.key] = 0;
    });

    if (!Object.keys(updates).length) return;

    log('removeWrapsForParent:updates', updates);

    await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    log('removeWrapsForParent:done');
  };

  const syncChildQuantityToParent = async (root, cartItems = []) => {
    const parentKey = root.dataset.parentKey || '';
    const selectedVariantIdRaw = String(root.dataset.selectedVariantId || '').trim();
    const desiredQuantity = getParentQuantity(root);

    if (!parentKey) return false;

    const wrapChildren = getWrapChildrenForParent(root, cartItems);
    if (!wrapChildren.length) return false;

    const selectedVariantId = selectedVariantIdRaw || String(wrapChildren[0].variant_id || '').trim();
    if (!selectedVariantId) return false;

    const totalWrapQuantity = getTotalWrapQuantity(wrapChildren);
    const isAlreadySingleMatchingChild =
      wrapChildren.length === 1
      && String(wrapChildren[0].variant_id) === selectedVariantId
      && Number(wrapChildren[0].quantity || 0) === desiredQuantity;

    if (isAlreadySingleMatchingChild) return false;
    if (totalWrapQuantity === desiredQuantity && wrapChildren.length <= 1) return false;

    await removeWrapsForParent(root, wrapChildren.map((child) => child.key));
    return addNestedWrap(selectedVariantId, parentKey, desiredQuantity);
  };

  const trimExcessWrapQuantity = async (root, cartItems = []) => {
    const desiredQuantity = getParentQuantity(root);
    const wrapChildren = getWrapChildrenForParent(root, cartItems);
    if (!wrapChildren.length) return false;

    const totalWrapQuantity = getTotalWrapQuantity(wrapChildren);
    let excess = totalWrapQuantity - desiredQuantity;
    if (excess <= 0) return false;

    const preferredVariantId = String(root.dataset.selectedVariantId || '').trim();
    const updates = {};

    const orderedChildren = [...wrapChildren].sort((a, b) => {
      const aPreferred = preferredVariantId && String(a.variant_id) === preferredVariantId;
      const bPreferred = preferredVariantId && String(b.variant_id) === preferredVariantId;
      if (aPreferred === bPreferred) return 0;
      return aPreferred ? 1 : -1;
    });

    orderedChildren.forEach((child) => {
      if (excess <= 0) return;

      const childQty = Math.max(0, Number(child.quantity) || 0);
      if (!childQty) return;

      if (childQty <= excess) {
        updates[child.key] = 0;
        excess -= childQty;
        return;
      }

      updates[child.key] = childQty - excess;
      excess = 0;
    });

    if (!Object.keys(updates).length) return false;

    await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    return true;
  };

  const syncGiftWrapRoots = async () => {
    if (rootSyncInProgress) return;
    rootSyncInProgress = true;

    try {
      const roots = Array.from(document.querySelectorAll('[data-gift-wrap-root]'));
      if (!roots.length) return;

      const cart = await fetchCart();
      const plans = [];

      roots.forEach((root) => {
        syncGiftWrapRootSelection(root, cart.items || []);

        const wrapChildren = getWrapChildrenForParent(root, cart.items || []);
        if (!wrapChildren.length) {
          didParentQuantityIncrease(root);
          return;
        }

        const desiredQuantity = getParentQuantity(root);
        const totalWrapQuantity = getTotalWrapQuantity(wrapChildren);
        const parentQuantityIncreased = didParentQuantityIncrease(root);

        if (totalWrapQuantity > desiredQuantity) {
          plans.push({ type: 'trim', root });
          return;
        }

        if (parentQuantityIncreased && totalWrapQuantity !== desiredQuantity) {
          plans.push({ type: 'align', root });
        }
      });

      if (!plans.length) return;

      let changed = false;

      for (const plan of plans) {
        // eslint-disable-next-line no-await-in-loop
        const synced = plan.type === 'trim'
          // eslint-disable-next-line no-await-in-loop
          ? await trimExcessWrapQuantity(plan.root, cart.items || [])
          // eslint-disable-next-line no-await-in-loop
          : await syncChildQuantityToParent(plan.root, cart.items || []);
        changed = changed || !!synced;
      }

      if (changed) {
        dispatchCartChange();
        refreshCartDrawer();
      }
    } catch (error) {
      logError('syncGiftWrapRoots failed', error);
    } finally {
      rootSyncInProgress = false;
    }
  };

  const scheduleRootSync = () => {
    if (rootSyncScheduled) return;
    rootSyncScheduled = true;

    setTimeout(async () => {
      rootSyncScheduled = false;
      await syncGiftWrapRoots();
    }, 0);
  };

  const setActiveOption = (root, variantId) => {
    log('setActiveOption', { parentKey: root?.dataset?.parentKey, variantId });
    root.querySelectorAll('[data-gift-wrap-option]').forEach((el) => {
      const isActive = el.dataset.variantId === String(variantId);
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  const resetGiftWrapRootUi = (root, checkbox, panel) => {
    if (checkbox) checkbox.checked = false;
    if (panel) panel.hidden = true;

    root.classList.remove('is-selected');
    root.dataset.selectedKeys = '';
    delete root.dataset.selectedKey;
    delete root.dataset.selectedVariantId;

    const selectedState = root.querySelector('[data-gift-wrap-selected]');
    if (selectedState) selectedState.hidden = true;

    setActiveOption(root, '');
  };

  document.addEventListener('click', (evt) => {
    const toggleRow = evt.target.closest('[data-gift-wrap-toggle-row]');
    if (!toggleRow) return;
    if (evt.target.closest('[data-gift-wrap-checkbox]')) return;

    const root = toggleRow.closest('[data-gift-wrap-root]');
    if (!root) return;

    const panel = root.querySelector('[data-gift-wrap-panel]');
    if (!panel) return;
    panel.hidden = !panel.hidden;
    root.querySelector('[data-gift-wrap-icon-toggle]')?.setAttribute('aria-expanded', String(!panel.hidden));
    // Carousels measured while hidden have no size; re-measure so nav arrows get the right state
    if (!panel.hidden) panel.querySelectorAll('carousel-slider').forEach((el) => el.refresh?.());
    log('toggle panel', { parentKey: root.dataset.parentKey, hidden: panel.hidden });
  });

  document.addEventListener('change', async (evt) => {
    const checkbox = evt.target.closest('[data-gift-wrap-checkbox]');
    if (!checkbox) return;

    const root = checkbox.closest('[data-gift-wrap-root]');
    if (!root) return;

    const panel = root.querySelector('[data-gift-wrap-panel]');
    const selectedKey = root.dataset.selectedKey || '';

    log('checkbox change', {
      parentKey: root.dataset.parentKey,
      checked: checkbox.checked,
      selectedKey,
      selectedVariantId: root.dataset.selectedVariantId || '',
    });

    if (checkbox.checked) {
      try {
        setRootLoading(root, true);
        if (panel) panel.hidden = false;
        root.classList.add('is-selected');
        openCartDrawer(checkbox);
        dispatchCartChange();
      } catch (error) {
        logError('checkbox checked flow failed', error);
        checkbox.checked = false;
      } finally {
        setRootLoading(root, false);
      }
      return;
    }

    try {
      setRootLoading(root, true);
      resetGiftWrapRootUi(root, checkbox, panel);
      await removeWrapsForParent(root, selectedKey ? [selectedKey] : []);
      dispatchCartChange();
      refreshCartDrawer();
    } catch (error) {
      logError('checkbox unchecked flow failed', error);
      checkbox.checked = true;
    } finally {
      setRootLoading(root, false);
    }
  });

  document.addEventListener('click', async (evt) => {
    const optionBtn = evt.target.closest('[data-gift-wrap-option]');
    if (!optionBtn) return;

    const root = optionBtn.closest('[data-gift-wrap-root]');
    if (!root) return;

    const parentKey = root.dataset.parentKey;
    const selectedKey = root.dataset.selectedKey || '';
    const selectedKeysRaw = root.dataset.selectedKeys || selectedKey;
    const selectedKeys = selectedKeysRaw
      .split(',')
      .map((key) => key.trim())
      .filter((key) => key.length);
    const variantId = optionBtn.dataset.variantId;
    const requestedQuantity = Math.max(1, Number(root.dataset.parentQuantity || '1') || 1);
    const currentSelectedVariantId = String(root.dataset.selectedVariantId || '').trim();

    log('option click', {
      parentKey,
      variantId,
      selectedKeys,
      requestedQuantity,
    });

    if (!parentKey || !variantId) return;

    if (String(variantId) === currentSelectedVariantId || optionBtn.classList.contains('is-active')) {
      return;
    }

    try {
      setRootLoading(root, true);
      setActiveOption(root, variantId);
      const addOk = await addNestedWrap(
        variantId,
        parentKey,
        requestedQuantity
      );
      if (addOk) {
        await removeWrapsForParent(root, selectedKeys, variantId);
        root.dataset.selectedVariantId = String(variantId);
        root.classList.add('is-selected');
        const checkbox = root.querySelector('[data-gift-wrap-checkbox]');
        const panel = root.querySelector('[data-gift-wrap-panel]');
        const selectedState = root.querySelector('[data-gift-wrap-selected]');
        if (checkbox) checkbox.checked = true;
        if (panel) panel.hidden = true;
        if (selectedState) selectedState.hidden = false;
        dispatchCartChange();
        refreshCartDrawer();
      } else {
        dispatchCartChange();
        refreshCartDrawer();
      }
    } catch (error) {
      logError('option click flow failed', error);
      // Keep current UI state until cart refreshes or user retries.
    } finally {
      setRootLoading(root, false);
    }
  });

  document.addEventListener('on:cart:after-merge', scheduleRootSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRootSync, { once: true });
  } else {
    scheduleRootSync();
  }
})();
